import { isAxiosError } from 'axios';
import { extractBackendMessage, extractDetailMessage } from '@/utils/apiError';

/** RFC 5987 ``filename*=utf-8'<lang>'<pct-encoded>`` — the charset is case-free. */
const EXTENDED_FILENAME = /filename\*\s*=\s*utf-8'[^']*'([^;\s]+)/i;

/** Plain ``filename="…"`` or ``filename=…``; the anchor keeps ``filename*`` out. */
const PLAIN_FILENAME = /(?:^|;)\s*filename\s*=\s*(?:"([^"]*)"|([^;]+))/i;

/**
 * The name a ``Content-Disposition`` header asks the file to be saved under,
 * or undefined when there is none. ``filename*`` wins over ``filename``, as
 * RFC 6266 has it; a malformed percent-encoding falls through to the plain one.
 */
export const filenameFromDisposition = (
  header?: string
): string | undefined => {
  if (!header) return undefined;

  const extended = EXTENDED_FILENAME.exec(header)?.[1];
  if (extended) {
    try {
      const decoded = decodeURIComponent(extended).trim();
      if (decoded) return decoded;
    } catch {
      // Not valid percent-encoding — try the plain name instead.
    }
  }

  const plain = PLAIN_FILENAME.exec(header);
  const name = (plain?.[1] ?? plain?.[2])?.trim();
  return name || undefined;
};

/** Hands ``blob`` to the browser as a download named ``filename``. */
export const saveBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/**
 * ``extractBackendMessage`` for a request made with ``responseType: 'blob'``:
 * there axios hands the JSON error body over as a ``Blob``, so it is read and
 * parsed first. Undefined when the body is not JSON or carries no ``detail``.
 */
export const blobErrorMessage = async (
  error: unknown
): Promise<string | undefined> => {
  if (!isAxiosError(error) || !(error.response?.data instanceof Blob)) {
    return extractBackendMessage(error);
  }

  try {
    const body: unknown = JSON.parse(await error.response.data.text());
    return extractDetailMessage(body);
  } catch {
    return undefined;
  }
};
